/**
 * ADR-0214 C7 — the Activity trail on screen. `TrailLinks` draws the steps as
 * links (each restores its step, query and all, with the trail up to it);
 * `useActivityTrail` gives a drill page the steps above it and the state its
 * links hand on.
 */

import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { readTrail, trailAbove, trailOnward, type TrailState, type TrailStep } from "../lib/activityTrail.js";

export function TrailLinks(props: { trail: TrailStep[] }) {
  return (
    <>
      {props.trail.map((s, i) => (
        <Fragment key={`${s.to}-${i}`}>
          {i > 0 ? " › " : null}
          <Link to={s.to} state={i ? ({ activityTrail: props.trail.slice(0, i) } satisfies TrailState) : undefined}>
            {s.label}
          </Link>
        </Fragment>
      ))}
    </>
  );
}

/**
 * A drill page's place on the trail. `fallback` is the trail above it when it
 * was opened from outside the drill (a pasted address, a bookmark).
 */
export function useActivityTrail(label: string, fallback: TrailStep[]): { above: TrailStep[]; onward: TrailState } {
  const loc = useLocation();
  const walked = readTrail(loc.state);
  const above = walked.length ? trailAbove(walked, loc.pathname) : fallback;
  return { above, onward: trailOnward(above, { to: `${loc.pathname}${loc.search}`, label }) };
}

/** The trail a page outside the drill was opened from (a study, a knowledge entry), or none. */
export function useWalkedTrail(): TrailStep[] {
  return readTrail(useLocation().state);
}
